import { useEffect, useId, useRef, type ReactNode } from "react";

export function PageHeader({ eyebrow, title, intro, actions }: { eyebrow: string; title: string; intro: string; actions?: ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{intro}</p></div>{actions ? <div className="header-actions">{actions}</div> : null}</header>;
}

export function StatusChip({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "teal" | "gold" | "red" | "blue" }) {
  return <span className={`status-chip ${tone}`}>{children}</span>;
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return <div className="empty-state"><strong>{title}</strong><span>{children}</span></div>;
}

export function Modal({ title, children, onClose, className = "" }: { title: string; children: ReactNode; onClose: () => void; className?: string }) {
  const dialog=useRef<HTMLElement>(null),titleId=useId(),returnFocus=useRef<HTMLElement|null>(null);
  useEffect(()=>{
    returnFocus.current=document.activeElement instanceof HTMLElement?document.activeElement:null;
    const element=dialog.current;
    const focusable=()=>[...(element?.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')??[])];
    (focusable()[0]??element)?.focus();
    function keydown(event:KeyboardEvent){
      if(event.key==="Escape"){event.preventDefault();onClose();return}
      if(event.key!=="Tab")return;
      const items=focusable();
      if(!items.length){event.preventDefault();element?.focus();return}
      const first=items[0],last=items[items.length-1];
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus()}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus()}
    }
    document.addEventListener("keydown",keydown);
    return()=>{document.removeEventListener("keydown",keydown);returnFocus.current?.focus()};
  },[onClose]);
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event)=>event.currentTarget===event.target&&onClose()}><section ref={dialog} tabIndex={-1} className={`modal ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId}><div className="modal-heading"><h2 id={titleId}>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Close dialog">×</button></div>{children}</section></div>;
}

export function formatDate(value?: string) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: value.includes("T") ? "short" : undefined }).format(date);
}
